import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, MapPin, Clock, Send, Users, HelpCircle, Handshake, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        }),
      });

      if (response.ok) {
        toast({
          title: language === 'ar' ? 'تم إرسال الرسالة' : 'Message Sent',
          description: language === 'ar' ? 'شكراً لتواصلك معنا. سنرد خلال 24 ساعة.' : 'Thank you for contacting us. We\'ll respond within 24 hours.',
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const emailContacts = [
    {
      label: language === 'ar' ? 'استفسارات عامة' : 'General Enquiries',
      email: 'enquiries@lumamedglobal.com',
      icon: Mail,
      description: language === 'ar' ? 'للأسئلة العامة والمعلومات' : 'For general questions and information'
    },
    {
      label: language === 'ar' ? 'الدعم' : 'Support',
      email: 'support@lumamedglobal.com',
      icon: HelpCircle,
      description: language === 'ar' ? 'الدعم الفني ومساعدة الحسابات' : 'Technical support and account help'
    },
    {
      label: language === 'ar' ? 'المعلومات' : 'Information',
      email: 'info@lumamedglobal.com',
      icon: Users,
      description: language === 'ar' ? 'تعرف على المزيد عن خدماتنا' : 'Learn more about our services'
    },
    {
      label: language === 'ar' ? 'الشراكات' : 'Partnerships',
      email: 'partnerships@lumamedglobal.com',
      icon: Handshake,
      description: language === 'ar' ? 'استفسارات الأعمال والشراكات' : 'Business and partnership enquiries'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          {/* Primary Contact - Phone */}
          <div className="mb-12">
            <Card className="border-2 border-[#42b1c2]/30 bg-gradient-to-r from-[#42b1c2]/5 to-[#edd982]/5">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                  <div className="w-16 h-16 bg-[#42b1c2] rounded-full flex items-center justify-center">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-center md:text-start">
                    <h2 className="text-lg font-semibold text-gray-700 mb-1">
                      {language === 'ar' ? 'اتصل بنا مباشرة' : 'Call Us Directly'}
                    </h2>
                    <a 
                      href="tel:+442035763953" 
                      className="text-3xl md:text-4xl font-bold text-[#42b1c2] hover:text-[#3a9fb0] transition-colors flex items-center gap-3"
                      data-testid="phone-link"
                    >
                      <span className="text-2xl">📞</span>
                      +44 20 3576 3953
                    </a>
                    <p className="text-gray-500 text-sm mt-2">
                      {language === 'ar' ? 'انقر للاتصال على الأجهزة المحمولة' : 'Click to call on mobile devices'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Email Contacts Grid */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              {language === 'ar' ? 'راسلنا بالبريد الإلكتروني' : 'Email Us'}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {emailContacts.map((contact) => {
                const IconComponent = contact.icon;
                return (
                  <Card key={contact.email} className="hover:shadow-lg transition-all hover:border-[#42b1c2]/50 group">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#42b1c2]/20 transition-colors">
                          <IconComponent className="h-6 w-6 text-[#42b1c2]" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{contact.label}</h3>
                        <a 
                          href={`mailto:${contact.email}`}
                          className="text-[#42b1c2] hover:text-[#3a9fb0] text-sm font-medium transition-colors break-all"
                          data-testid={`email-${contact.label.toLowerCase().replace(' ', '-')}`}
                        >
                          {contact.email}
                        </a>
                        <p className="text-gray-500 text-xs mt-2">{contact.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Location Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#42b1c2]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {language === 'ar' ? 'مكتب المملكة المتحدة' : 'United Kingdom Office'}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {language === 'ar' ? 'لندن، المملكة المتحدة' : 'London, United Kingdom'}
                    </p>
                    <a 
                      href="tel:+442035763953" 
                      className="text-[#42b1c2] hover:text-[#3a9fb0] text-sm font-medium mt-2 inline-flex items-center gap-1"
                    >
                      <Phone className="h-3 w-3" />
                      +44 20 3576 3953
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <div className="w-12 h-12 bg-[#42b1c2]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#42b1c2]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {language === 'ar' ? 'عمليات الشرق الأوسط' : 'Middle East Operations'}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {language === 'ar' ? 'المملكة العربية السعودية ومنطقة الخليج' : 'Saudi Arabia & GCC Region'}
                    </p>
                    <p className="text-gray-500 text-xs mt-2">
                      {language === 'ar' ? 'نخدم مرافق الرعاية الصحية في جميع أنحاء المنطقة' : 'Serving healthcare facilities across the region'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form & Office Hours */}
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">{t('contact.nameLabel')}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={language === 'ar' ? 'أحمد محمد' : 'John Smith'}
                    required
                    className="mt-2"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t('contact.emailLabel')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="mt-2"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">{t('contact.subjectLabel')}</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={language === 'ar' ? 'كيف يمكننا مساعدتك؟' : 'How can we help?'}
                    required
                    className="mt-2"
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <Label htmlFor="message">{t('contact.messageLabel')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={language === 'ar' ? 'أخبرنا المزيد عن استفسارك...' : 'Tell us more about your enquiry...'}
                    required
                    rows={6}
                    className="mt-2"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white"
                  data-testid="contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 me-2 animate-spin" />
                      {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 me-2" />
                      {t('contact.submitBtn')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {language === 'ar' ? 'ساعات العمل' : 'Office Hours'}
              </h2>
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4 rtl:space-x-reverse">
                      <Clock className="h-6 w-6 text-[#42b1c2] mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {language === 'ar' ? 'مكتب المملكة المتحدة' : 'UK Office'}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {language === 'ar' ? 'الإثنين - الجمعة: 9:00 صباحاً - 5:00 مساءً (GMT)' : 'Monday - Friday: 9:00 AM - 5:00 PM (GMT)'}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {language === 'ar' ? 'السبت - الأحد: مغلق' : 'Saturday - Sunday: Closed'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4 rtl:space-x-reverse">
                      <Clock className="h-6 w-6 text-[#42b1c2] mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {language === 'ar' ? 'دعم الشرق الأوسط' : 'Middle East Support'}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {language === 'ar' ? 'الأحد - الخميس: 9:00 صباحاً - 6:00 مساءً (AST)' : 'Sunday - Thursday: 9:00 AM - 6:00 PM (AST)'}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {language === 'ar' ? 'الجمعة - السبت: مغلق' : 'Friday - Saturday: Closed'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-gradient-to-br from-[#42b1c2]/10 to-[#edd982]/10 rounded-2xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {language === 'ar' ? 'تحتاج مساعدة فورية؟' : 'Need Immediate Help?'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {language === 'ar' ? 'للأمور العاجلة، اتصل بنا عبر الهاتف أو البريد الإلكتروني. نهدف للرد على جميع الاستفسارات خلال 24 ساعة.' : 'For urgent matters, contact us via phone or email. We aim to respond to all enquiries within 24 hours.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a 
                      href="tel:+442035763953"
                      className="inline-flex items-center justify-center px-4 py-2 bg-[#42b1c2] text-white rounded-lg hover:bg-[#3a9fb0] transition-colors text-sm font-medium"
                    >
                      <Phone className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'اتصل الآن' : 'Call Now'}
                    </a>
                    <a 
                      href="mailto:support@lumamedglobal.com"
                      className="inline-flex items-center justify-center px-4 py-2 border border-[#42b1c2] text-[#42b1c2] rounded-lg hover:bg-[#42b1c2] hover:text-white transition-colors text-sm font-medium"
                    >
                      <Mail className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'راسل الدعم' : 'Email Support'}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
